import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const fmt = (n) => n != null ? Math.round(n).toLocaleString('fr-FR') : '-';
const fmtUsd = (n) => n != null ? `$${Number(n).toFixed(2)}` : '-';

const SortHeader = ({ label, field, sortField, sortDir, onSort, style }) => {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{
        padding: '10px 8px', textAlign: 'right', fontSize: '11px', fontWeight: 600,
        color: active ? '#f97316' : 'rgba(255,255,255,0.5)', cursor: 'pointer',
        whiteSpace: 'nowrap', userSelect: 'none', borderBottom: '1px solid #1f1f1f',
        position: 'sticky', top: 0, backgroundColor: '#0d0d0d', zIndex: 1, ...style,
      }}
    >
      {label} {active ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
    </th>
  );
};

const OpportunitesBonusSection = ({ data = [], loading, serverColor = '#f97316', basePath = '/admin/creators' }) => {
  const { t } = useTranslation();
  const [rankupConfig, setRankupConfig] = useState([]);
  const [activenessConfig, setActivenessConfig] = useState([]);
  const [sortField, setSortField] = useState('type_order');
  const [sortDir, setSortDir] = useState('asc');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    const load = async () => {
      const [rcRes, acRes] = await Promise.all([
        supabase.from('rankup_config').select('server, tier, min_diamonds, ratio_rankup, ratio_maintain, min_live_days, min_live_hours'),
        supabase.from('activeness_config').select('server, level, min_live_days, min_live_hours, bonus_ratio'),
      ]);
      setRankupConfig(rcRes.data || []);
      setActivenessConfig(acRes.data || []);
    };
    load();
  }, []);

  const today = new Date();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const daysRemaining = lastDayOfMonth - today.getDate();

  const opportunities = useMemo(() => {
    if (!data.length || !rankupConfig.length || !activenessConfig.length) return [];

    const getThreshold = (server, tier) => {
      const m = rankupConfig.find(r => r.server === server && r.tier === tier);
      return m ? Number(m.min_diamonds) : null;
    };
    const getRc = (server, tier) => rankupConfig.find(r => r.server === server && r.tier === tier);
    const getAc = (server, level) => activenessConfig.find(r => r.server === server && r.level === level);

    const results = [];

    data.forEach(c => {
      if (!c.is_active) return;
      const server = c.server;
      const tierM1 = c.tier_m1 ?? 1;
      const diamonds = c.diamonds || 0;
      const liveDays = c.live_days || 0;
      const liveHours = c.live_duration_minutes ? Math.round(c.live_duration_minutes / 60) : 0;
      const currentActLevel = c.activeness_level || 0;

      // --- ACTIVENESS opportunities ---
      // Check next activeness level — only show if at 70%+ of days/hours needed
      for (let lvl = currentActLevel + 1; lvl <= 5; lvl++) {
        const ac = getAc(server, lvl);
        if (!ac) continue;
        const reqDays = ac.min_live_days || 0;
        const reqHours = Number(ac.min_live_hours || 0);
        const daysNeeded = Math.max(0, reqDays - liveDays);
        const hoursNeeded = Math.max(0, reqHours - liveHours);
        // Only show if achievable in remaining days
        if (daysNeeded > daysRemaining) continue;
        // Only show if at 70%+ progress
        const daysPct = reqDays > 0 ? liveDays / reqDays : 1;
        const hoursPct = reqHours > 0 ? liveHours / reqHours : 1;
        if (daysPct < 0.7 && hoursPct < 0.7) continue;
        // Estimate bonus in diamonds
        const bonusDiamonds = Math.round(diamonds * Number(ac.bonus_ratio || 0) / 100);
        // Current activeness bonus (if any)
        const currentBonusDiamonds = currentActLevel > 0
          ? Math.round(diamonds * Number(getAc(server, currentActLevel)?.bonus_ratio || 0) / 100)
          : 0;
        const deltaBonusDiamonds = bonusDiamonds - currentBonusDiamonds;
        if (deltaBonusDiamonds <= 0) continue;
        // Convert to USD: 100 diamonds = $1
        const deltaBonusUsd = deltaBonusDiamonds / 100;

        results.push({
          tiktok_id: c.tiktok_id,
          username: c.username || '',
          display_name: c.display_name || '',
          server,
          manager_name: c.manager_name || '',
          diamonds,
          tier_current: c.tier_current,
          tier_m1: tierM1,
          live_days: liveDays,
          live_hours: liveHours,
          activeness_level: currentActLevel,
          type: 'ACTIVENESS',
          type_order: 1,
          target_label: `L${currentActLevel} \u2192 L${lvl}`,
          days_needed: daysNeeded,
          hours_needed: hoursNeeded,
          diamonds_needed: 0,
          bonus_diamonds: deltaBonusDiamonds,
          bonus_usd: deltaBonusUsd,
          achievable: daysNeeded <= daysRemaining,
          level: c.level,
        });
        break; // Only show next achievable level
      }

      // --- MAINTIEN opportunity (checked BEFORE rankup) ---
      const seuilNext = getThreshold(server, tierM1 + 1);
      const rcM1 = getRc(server, tierM1);
      let hasMaintenOpportunity = false;

      if (tierM1 >= 3) {
        const seuilCurr = getThreshold(server, tierM1);
        if (seuilCurr && diamonds < seuilCurr && rcM1) {
          const diamondsNeeded = seuilCurr - diamonds;
          const progressPct = seuilCurr > 0 ? diamonds / seuilCurr : 0;
          const daysLiveNeeded = Math.max(0, (Number(rcM1.min_live_days) || 0) - liveDays);
          const hoursLiveNeeded = Math.max(0, (Number(rcM1.min_live_hours) || 0) - liveHours);
          // Only show if at 70%+ of diamonds objective and live days achievable
          if (progressPct >= 0.7 && daysLiveNeeded <= daysRemaining) {
            const bonusDiamonds = Math.round(seuilCurr * (Number(rcM1.ratio_maintain) || 0) / 100);
            const bonusUsd = bonusDiamonds / 100;
            results.push({
              tiktok_id: c.tiktok_id,
              username: c.username || '',
              display_name: c.display_name || '',
              server,
              manager_name: c.manager_name || '',
              diamonds,
              tier_current: c.tier_current,
              tier_m1: tierM1,
              live_days: liveDays,
              live_hours: liveHours,
              activeness_level: currentActLevel,
              type: 'MAINTIEN',
              type_order: 3,
              target_label: `${t('admin.performanceActuelle.maintain', 'Maintain')} T${tierM1}`,
              days_needed: daysLiveNeeded,
              hours_needed: hoursLiveNeeded,
              diamonds_needed: diamondsNeeded,
              bonus_diamonds: bonusDiamonds,
              bonus_usd: bonusUsd,
              achievable: true,
              level: c.level,
            });
            hasMaintenOpportunity = true;
          }
        }
      }

      // --- RANKUP opportunity (only if NO maintien opportunity for this creator) ---
      if (!hasMaintenOpportunity && seuilNext && rcM1 && diamonds < seuilNext) {
        const diamondsNeeded = seuilNext - diamonds;
        const progressPct = seuilNext > 0 ? diamonds / seuilNext : 0;
        const daysLiveNeeded = Math.max(0, (Number(rcM1.min_live_days) || 0) - liveDays);
        const hoursLiveNeeded = Math.max(0, (Number(rcM1.min_live_hours) || 0) - liveHours);
        // Only show if at 70%+ of diamonds objective and live days achievable
        if (progressPct >= 0.7 && daysLiveNeeded <= daysRemaining) {
          const bonusDiamonds = Math.round(seuilNext * (Number(rcM1.ratio_rankup) || 0) / 100);
          const bonusUsd = bonusDiamonds / 100;
          results.push({
            tiktok_id: c.tiktok_id,
            username: c.username || '',
            display_name: c.display_name || '',
            server,
            manager_name: c.manager_name || '',
            diamonds,
            tier_current: c.tier_current,
            tier_m1: tierM1,
            live_days: liveDays,
            live_hours: liveHours,
            activeness_level: currentActLevel,
            type: 'RANKUP',
            type_order: 2,
            target_label: `T${tierM1} \u2192 T${tierM1 + 1}`,
            days_needed: daysLiveNeeded,
            hours_needed: hoursLiveNeeded,
            diamonds_needed: diamondsNeeded,
            bonus_diamonds: bonusDiamonds,
            bonus_usd: bonusUsd,
            achievable: true,
            level: c.level,
          });
        }
      }
    });

    return results;
  }, [data, rankupConfig, activenessConfig, daysRemaining]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'type_order' || field === 'days_needed' ? 'asc' : 'desc'); }
  };

  const filtered = useMemo(() => {
    let list = [...opportunities];
    if (filterType !== 'all') list = list.filter(r => r.type === filterType);
    list.sort((a, b) => {
      const va = a[sortField] ?? 0;
      const vb = b[sortField] ?? 0;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [opportunities, sortField, sortDir, filterType]);

  const typeColor = (type) => {
    switch (type) {
      case 'ACTIVENESS': return '#8b5cf6';
      case 'RANKUP': return '#22c55e';
      case 'MAINTIEN': return '#3b82f6';
      default: return '#fff';
    }
  };

  const totals = useMemo(() => ({
    bonus_diamonds: filtered.reduce((s, r) => s + r.bonus_diamonds, 0),
    bonus_usd: filtered.reduce((s, r) => s + r.bonus_usd, 0),
    count: filtered.length,
    activeness: filtered.filter(r => r.type === 'ACTIVENESS').length,
    rankup: filtered.filter(r => r.type === 'RANKUP').length,
    maintien: filtered.filter(r => r.type === 'MAINTIEN').length,
  }), [filtered]);

  if (loading || !opportunities.length) {
    if (loading) return (
      <div className="rounded-xl border p-6" style={{ backgroundColor: '#111', borderColor: '#1f1f1f' }}>
        <div className="h-4 rounded animate-pulse mb-3" style={{ backgroundColor: '#1f1f1f', width: '200px' }} />
        <div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: '300px' }} />
      </div>
    );
    return null;
  }

  return (
    <div>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
        {[
          { label: t('admin.performanceActuelle.opportunities', 'Opportunities'), value: totals.count, color: '#fff' },
          { label: 'Activeness', value: totals.activeness, color: '#8b5cf6' },
          { label: 'Rankup', value: totals.rankup, color: '#22c55e' },
          { label: t('admin.performanceActuelle.maintain', 'Maintain'), value: totals.maintien, color: '#3b82f6' },
          { label: t('admin.performanceActuelle.potentialBonus', 'Potential bonus'), value: fmt(totals.bonus_diamonds), color: '#f97316', sub: 'diamonds' },
          { label: t('admin.performanceActuelle.estimateUsd', 'Estimate $'), value: fmtUsd(totals.bonus_usd), color: '#22c55e' },
        ].map((kpi, i) => (
          <div key={i} className="rounded-xl border p-3" style={{ backgroundColor: '#111', borderColor: '#1f1f1f' }}>
            <div className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{kpi.label}</div>
            <div className="text-lg font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
            {kpi.sub && <div className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{kpi.sub}</div>}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.performanceActuelle.daysBeforeEnd', { days: daysRemaining })}</span>
        <div className="flex gap-2 ml-auto">
          {[
            { key: 'all', label: t('admin.performanceActuelle.filterAll', 'All'), color: '#f97316' },
            { key: 'ACTIVENESS', label: 'Activeness', color: '#8b5cf6' },
            { key: 'RANKUP', label: 'Rankup', color: '#22c55e' },
            { key: 'MAINTIEN', label: t('admin.performanceActuelle.maintain', 'Maintain'), color: '#3b82f6' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterType(f.key)}
              className="px-3 py-1 rounded-lg text-xs font-medium"
              style={{
                backgroundColor: filterType === f.key ? f.color : '#1a1a1a',
                color: filterType === f.key ? '#000' : 'rgba(255,255,255,0.5)',
                border: '1px solid #2a2a2a',
              }}
            >
              {f.label} ({f.key === 'all' ? opportunities.length : opportunities.filter(r => r.type === f.key).length})
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111', borderColor: '#1f1f1f' }}>
        <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
          <table className="w-full" style={{ fontSize: '12px', borderCollapse: 'collapse', minWidth: '1100px' }}>
            <thead>
              <tr>
                <SortHeader label={t('admin.performanceActuelle.colCreator', 'Creator')} field="username" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={{ textAlign: 'left', position: 'sticky', left: 0, backgroundColor: '#0d0d0d', zIndex: 2 }} />
                <SortHeader label={t('admin.performanceActuelle.colServer', 'Server')} field="server" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Manager" field="manager_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Diamonds" field="diamonds" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colDays', 'Days')} field="live_days" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colHours', 'Hours')} field="live_hours" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Type" field="type_order" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colObjective', 'Objective')} field="target_label" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colMissingDays', 'Missing days')} field="days_needed" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colMissingHours', 'Missing hours')} field="hours_needed" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colMissingDiamonds', 'Missing diamonds')} field="diamonds_needed" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colBonusDia', 'Bonus (Dia.)')} field="bonus_diamonds" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('admin.performanceActuelle.colBonusUsd', 'Bonus ($)')} field="bonus_usd" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={`${r.tiktok_id}-${r.type}-${i}`} style={{ borderBottom: '1px solid #1a1a1a' }}>
                  <td className="px-2 py-2 font-medium" style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, backgroundColor: '#111', zIndex: 1 }}>
                    <a href={`${basePath}/${r.tiktok_id}`} style={{ color: '#fff', textDecoration: 'none' }}>
                      {r.username || r.tiktok_id.slice(-8)}
                    </a>
                  </td>
                  <td className="px-2 py-2 text-right" style={{ color: serverColor }}>{r.server}</td>
                  <td className="px-2 py-2 text-right" style={{ color: 'rgba(255,255,255,0.6)' }}>{r.manager_name}</td>
                  <td className="px-2 py-2 text-right font-medium">{fmt(r.diamonds)}</td>
                  <td className="px-2 py-2 text-right">{r.live_days}</td>
                  <td className="px-2 py-2 text-right">{r.live_hours}h</td>
                  <td className="px-2 py-2 text-right">
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold" style={{ color: '#000', backgroundColor: typeColor(r.type) }}>
                      {r.type}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-medium" style={{ color: typeColor(r.type) }}>{r.target_label}</td>
                  <td className="px-2 py-2 text-right" style={{ color: r.days_needed > 0 ? '#fbbf24' : '#22c55e' }}>
                    {r.days_needed > 0 ? `${r.days_needed}${t('admin.dashboard.daysShort', 'd')}` : '\u2713'}
                  </td>
                  <td className="px-2 py-2 text-right" style={{ color: r.hours_needed > 0 ? '#fbbf24' : '#22c55e' }}>
                    {r.hours_needed > 0 ? `${r.hours_needed}h` : '\u2713'}
                  </td>
                  <td className="px-2 py-2 text-right" style={{ color: r.diamonds_needed > 0 ? '#fbbf24' : 'rgba(255,255,255,0.2)' }}>
                    {r.diamonds_needed > 0 ? fmt(r.diamonds_needed) : '-'}
                  </td>
                  <td className="px-2 py-2 text-right font-bold" style={{ color: '#f97316' }}>{fmt(r.bonus_diamonds)}</td>
                  <td className="px-2 py-2 text-right font-bold" style={{ color: '#22c55e' }}>{fmtUsd(r.bonus_usd)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #333', backgroundColor: '#0a0a0a' }}>
                <td className="px-2 py-3 font-bold" style={{ position: 'sticky', left: 0, backgroundColor: '#0a0a0a', zIndex: 1 }}>
                  TOTAL ({filtered.length})
                </td>
                <td colSpan={10} />
                <td className="px-2 py-3 text-right font-bold" style={{ color: '#f97316' }}>{fmt(totals.bonus_diamonds)}</td>
                <td className="px-2 py-3 text-right font-bold" style={{ color: '#22c55e' }}>{fmtUsd(totals.bonus_usd)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OpportunitesBonusSection;
